import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command';
import {
  ArrowLeft, Save, History, FileText, Link, Eye, Pencil,
  MessageSquare, Send, Trash2, User, ExternalLink, Database, Globe, ChevronDown, Columns
} from 'lucide-react';
import { toast } from 'sonner';

function ScopePicker({ currentScope, linkedSchemas, onSwitch, showGlobalSide, onToggleSide, hasSchemaScope }) {
  const [open, setOpen] = useState(false);
  const currentLabel = currentScope === '_global' ? 'Global' : linkedSchemas.find(s => s.schema_id === currentScope)?.name || currentScope;

  return (
    <div className="flex items-center gap-2 mb-6" data-testid="scope-switcher">
      <button onClick={() => onSwitch('_global')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors ${currentScope === '_global' ? 'bg-[#0A0A0A] text-white' : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#F9FAFB]'}`} data-testid="scope-global">
        <Globe className="w-3 h-3" /> Global
      </button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors ${currentScope !== '_global' ? 'bg-[#0A0A0A] text-white' : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#F9FAFB]'}`} data-testid="scope-schema-picker">
            <Database className="w-3 h-3" /> {currentScope !== '_global' ? currentLabel : 'Select schema...'} <ChevronDown className="w-3 h-3 ml-1" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 rounded-none w-64" align="start">
          <Command className="rounded-none">
            <CommandInput placeholder="Search schemas..." className="h-8 text-xs rounded-none" />
            <CommandList className="max-h-48">
              <CommandEmpty className="py-3 text-xs text-center text-[#9CA3AF]">No schemas</CommandEmpty>
              {linkedSchemas.map(s => (
                <CommandItem key={s.schema_id} value={s.name} onSelect={() => { onSwitch(s.schema_id); setOpen(false); }} className="text-xs py-1.5 rounded-none cursor-pointer">
                  <Database className="w-3 h-3 mr-1.5 text-[#002FA7]" /> {s.name}
                  {currentScope === s.schema_id && <span className="ml-auto text-[10px] text-[#002FA7]">Active</span>}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {hasSchemaScope && (
        <button onClick={onToggleSide} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors ${showGlobalSide ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-[#4B5563] border-[#E5E7EB]'}`} data-testid="toggle-side-by-side">
          <Columns className="w-3 h-3" /> {showGlobalSide ? 'Hide Global' : 'Show Global'}
        </button>
      )}
    </div>
  );
}

function GlobalSidePanel({ globalDoc }) {
  if (!globalDoc) return null;
  return (
    <div className="border border-[#E5E7EB] bg-[#F9FAFB]" data-testid="global-side-panel">
      <div className="px-4 py-2 border-b border-[#E5E7EB] flex items-center gap-1.5">
        <Globe className="w-3 h-3 text-[#9CA3AF]" />
        <span className="text-xs font-semibold text-[#4B5563]">Global Document</span>
        {globalDoc.version > 0 && <span className="text-[10px] text-[#9CA3AF] ml-auto">v{globalDoc.version}</span>}
      </div>
      {globalDoc.google_doc_url && (
        <div className="px-4 py-2 border-b border-[#E5E7EB]">
          <a href={globalDoc.google_doc_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"><Link className="w-3 h-3" /> Google Doc <ExternalLink className="w-2.5 h-2.5" /></a>
        </div>
      )}
      <div className="p-4 prose prose-sm max-w-none overflow-auto max-h-[500px] text-xs">
        {globalDoc.content ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{globalDoc.content}</ReactMarkdown> : <p className="text-[#9CA3AF] italic">No global content yet.</p>}
      </div>
    </div>
  );
}

export default function EntityDocPage() {
  const { entityId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission, user: currentUser } = useAuth();
  const canEdit = hasPermission('edit_elements');

  const schemaId = searchParams.get('schema') || null;
  const [doc, setDoc] = useState(null);
  const [globalDoc, setGlobalDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [googleUrl, setGoogleUrl] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [versions, setVersions] = useState([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showGlobalSide, setShowGlobalSide] = useState(false);

  const fetchDoc = useCallback(async () => {
    setLoading(true);
    try {
      const params = schemaId ? `?schema_id=${schemaId}` : '';
      const r = await api.get(`/entities/${entityId}/doc${params}`);
      setDoc(r.data);
      setTitle(r.data.title || '');
      setContent(r.data.content || '');
      setGoogleUrl(r.data.google_doc_url || '');
      setDirty(false);
      setVersionsOpen(false);
      // If viewing schema-specific, also load global for side-by-side
      if (schemaId) {
        try {
          const g = await api.get(`/entities/${entityId}/doc`);
          setGlobalDoc(g.data);
        } catch { setGlobalDoc(null); }
      } else {
        setGlobalDoc(null);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [entityId, schemaId]);

  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const params = schemaId ? `?schema_id=${schemaId}` : '';
      await api.put(`/entities/${entityId}/doc${params}`, { title, content, google_doc_url: googleUrl });
      toast.success('Saved');
      setDirty(false);
      fetchDoc();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const fetchVersions = async () => {
    try {
      const params = schemaId ? `?schema_id=${schemaId}` : '';
      const r = await api.get(`/entities/${entityId}/doc/versions${params}`);
      setVersions(r.data);
      setVersionsOpen(true);
    } catch { toast.error('Failed to load versions'); }
  };

  const restoreVersion = (v) => {
    setContent(v.content_snapshot || '');
    if (v.title_snapshot) setTitle(v.title_snapshot);
    setDirty(true);
    toast.success(`Restored v${v.version}`);
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const params = schemaId ? `?schema_id=${schemaId}` : '';
      await api.post(`/entities/${entityId}/doc/comments${params}`, { text: commentText.trim() });
      setCommentText('');
      fetchDoc();
      toast.success('Comment added');
    } catch (err) { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteComment = async (cid) => {
    try {
      await api.delete(`/entities/${entityId}/doc/comments/${cid}`);
      fetchDoc();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const switchScope = (newSchemaId) => {
    if (newSchemaId === '_global') {
      setSearchParams({});
    } else {
      setSearchParams({ schema: newSchemaId });
    }
  };

  const getGoogleEmbedUrl = (url) => {
    if (!url) return null;
    const m = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    return m ? `https://docs.google.com/document/d/${m[1]}/preview` : null;
  };

  if (loading) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div></div>);
  }
  if (!doc) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="max-w-4xl mx-auto px-6 py-12 text-center text-[#9CA3AF]">Entity not found</div></div>);
  }

  const embedUrl = getGoogleEmbedUrl(googleUrl);
  const currentScope = doc.current_scope || '_global';
  const linkedSchemas = doc.linked_schemas || [];

  return (
    <div className="min-h-screen bg-white" data-testid="entity-doc-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/entities')} className="h-7 px-2 rounded-none text-[#4B5563]" data-testid="back-to-entities">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Entities
          </Button>
          <span className="text-[#9CA3AF]">/</span>
          <span className="text-sm text-[#4B5563]">{doc.entity_name}</span>
          <span className="text-[#9CA3AF]">/</span>
          <span className="text-sm font-medium text-[#0A0A0A]">Documentation</span>
        </div>

        {/* Scope Switcher */}
        <ScopePicker
          currentScope={currentScope}
          linkedSchemas={linkedSchemas}
          onSwitch={switchScope}
          showGlobalSide={showGlobalSide && !!schemaId}
          onToggleSide={() => setShowGlobalSide(p => !p)}
          hasSchemaScope={!!schemaId}
        />

        {/* Header */}
        <div className="border-b border-[#E5E7EB] pb-5 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {canEdit ? (
                <Input value={title} onChange={e => { setTitle(e.target.value); setDirty(true); }} className="text-2xl font-bold rounded-none border-0 border-b border-transparent hover:border-[#E5E7EB] focus:border-[#002FA7] px-0 h-auto py-1" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="doc-title-input" />
              ) : (
                <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{title}</h1>
              )}
              <div className="flex items-center gap-3 mt-2 text-xs text-[#9CA3AF]">
                {doc.version > 0 && <span>v{doc.version}</span>}
                {doc.updated_by_name && <span>Last edited by {doc.updated_by_name}</span>}
                {doc.updated_at && <span>{new Date(doc.updated_at).toLocaleDateString()}</span>}
                <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-600 text-[10px]">{currentScope === '_global' ? 'Global' : linkedSchemas.find(s => s.schema_id === currentScope)?.name || currentScope}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {doc.version_count > 0 && (
                <Button variant="outline" size="sm" onClick={fetchVersions} className="rounded-none text-xs h-8" data-testid="doc-versions-btn">
                  <History className="w-3.5 h-3.5 mr-1.5" /> History ({doc.version_count})
                </Button>
              )}
              {dirty && canEdit && (
                <Button onClick={handleSave} disabled={saving} className="rounded-none bg-[#002FA7] hover:bg-[#002585] text-xs h-8" data-testid="save-doc-btn">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Google Docs link */}
        <div className="mb-6">
          <Label className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-[0.2em]">Linked Google Doc</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Link className="w-3.5 h-3.5 text-[#9CA3AF] shrink-0" />
            {canEdit ? (
              <Input value={googleUrl} onChange={e => { setGoogleUrl(e.target.value); setDirty(true); }} placeholder="https://docs.google.com/document/d/..." className="rounded-none text-sm flex-1" data-testid="google-doc-input" />
            ) : (
              <span className="text-sm text-[#4B5563]">{googleUrl || 'No linked document'}</span>
            )}
            {googleUrl && (
              <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline text-xs flex items-center gap-1" data-testid="google-doc-link">
                Open <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          {embedUrl && <div className="mt-3 border border-[#E5E7EB]" data-testid="google-doc-embed"><iframe src={embedUrl} title="Google Doc" className="w-full h-[400px] border-0" /></div>}
        </div>

        {/* Version History */}
        {versionsOpen && versions.length > 0 && (
          <div className="mb-6 border border-[#E5E7EB]" data-testid="doc-version-history">
            <div className="flex items-center justify-between px-4 py-2 bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF]"><History className="w-3 h-3 inline mr-1" /> Version History</span>
              <Button variant="ghost" size="sm" onClick={() => setVersionsOpen(false)} className="text-xs h-6 rounded-none">Close</Button>
            </div>
            {versions.map(v => (
              <div key={v.version_id} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#E5E7EB] last:border-b-0">
                <span className="text-xs font-bold min-w-[30px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>v{v.version}</span>
                <User className="w-3 h-3 text-[#9CA3AF]" /><span className="text-xs text-[#4B5563]">{v.changed_by_name}</span>
                <span className="text-xs text-[#9CA3AF]">{new Date(v.changed_at).toLocaleString()}</span>
                <div className="flex gap-1 flex-1">{(v.changed_fields || []).map(f => (<span key={f} className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700">{f}</span>))}</div>
                {canEdit && <Button variant="ghost" size="sm" className="text-[10px] h-6 rounded-none text-[#002FA7]" onClick={() => restoreVersion(v)}>Restore</Button>}
              </div>
            ))}
          </div>
        )}

        {/* Markdown Editor + optional global side panel */}
        <div className={showGlobalSide && schemaId ? 'grid grid-cols-2 gap-4 mb-8' : 'mb-8'}>
          <div>
            <Tabs value={canEdit ? activeTab : 'preview'} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF]"><FileText className="w-3.5 h-3.5 inline mr-1" /> {currentScope === '_global' ? 'Document Content' : 'Schema-Specific Content'}</span>
                {canEdit && (
                  <TabsList className="h-7 rounded-none">
                    <TabsTrigger value="edit" className="text-[10px] rounded-none h-6 px-3"><Pencil className="w-3 h-3 mr-1" /> Edit</TabsTrigger>
                    <TabsTrigger value="preview" className="text-[10px] rounded-none h-6 px-3"><Eye className="w-3 h-3 mr-1" /> Preview</TabsTrigger>
                  </TabsList>
                )}
              </div>
              <TabsContent value="edit" className="mt-0">
                <Textarea value={content} onChange={e => { setContent(e.target.value); setDirty(true); }} className="rounded-none font-mono text-sm min-h-[400px] resize-y" data-testid="doc-content-editor" placeholder="Write documentation in Markdown..." />
              </TabsContent>
              <TabsContent value="preview" className="mt-0">
                <div className="border border-[#E5E7EB] p-6 min-h-[400px] prose prose-sm max-w-none" data-testid="doc-content-preview">
                  {content ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown> : <p className="text-[#9CA3AF] italic">No content yet.</p>}
                </div>
              </TabsContent>
            </Tabs>
          </div>
          {showGlobalSide && schemaId && <GlobalSidePanel globalDoc={globalDoc} />}
        </div>

        {dirty && <p className="text-[10px] text-amber-600 mb-4 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Unsaved changes</p>}

        {/* Comments */}
        <div data-testid="doc-comments">
          <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF] mb-3"><MessageSquare className="w-3.5 h-3.5 inline mr-1" /> Comments ({doc.comments?.length || 0})</h2>
          <div className="flex gap-2 mb-4">
            <Textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Add a comment..." className="rounded-none text-sm flex-1 min-h-[50px]" data-testid="doc-comment-input" rows={2} />
            <Button onClick={handleAddComment} disabled={submitting || !commentText.trim()} className="rounded-none bg-[#0A0A0A] hover:bg-[#171717] self-end" data-testid="doc-comment-submit"><Send className="w-3.5 h-3.5" /></Button>
          </div>
          {(doc.comments || []).length === 0 ? (
            <div className="border border-dashed border-[#E5E7EB] py-6 text-center text-sm text-[#9CA3AF]">No comments yet.</div>
          ) : (
            <div className="border border-[#E5E7EB]">
              {doc.comments.map(c => (
                <div key={c.comment_id} className="px-4 py-3 border-b border-[#E5E7EB] last:border-b-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2"><User className="w-3 h-3 text-[#9CA3AF]" /><span className="text-xs font-medium text-[#0A0A0A]">{c.user_name}</span><span className="text-[10px] text-[#9CA3AF]">{new Date(c.created_at).toLocaleString()}</span></div>
                    {c.user_id === currentUser?.user_id && <Button variant="ghost" size="sm" className="h-5 w-5 p-0 rounded-none text-[#9CA3AF] hover:text-red-500" onClick={() => handleDeleteComment(c.comment_id)}><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                  <p className="text-sm text-[#4B5563] pl-5">{c.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
